import {useCallback, useEffect} from 'react';
import paper from 'paper';
import { calculateMMBounds } from '../use-item-bounds';
import { TRANSFORM_OVERLAY_HANDLE_SIZE_PX } from '@/constants/editor';


export function useTransformOverlay(theme = 'dark') {
  const updateTransformUI = useCallback(() => {
    if (!paper.project || !paper.view) return;

    const isDark = theme === 'dark';
    const primaryColor = isDark ? '#06b6d4' : '#4f46e5';

    const oldUI = paper.project.getItem({name: 'transformOverlay'});
    if (oldUI) oldUI.remove();

    const selectedItems = paper.project.selectedItems.filter(
      (item) => !item.data?.isGuide && !item.guide && !item.data?.isTransformHandle && item.visible !== false
    );

    if (selectedItems.length === 0) {
      paper.view.update();
      return;
    }

    let bounds = selectedItems[0].bounds.clone();

    for (let i = 1; i < selectedItems.length; i++) {
      bounds = bounds.unite(selectedItems[i].bounds);
    }

    const zoom = paper.view.zoom;
    const handleSize = TRANSFORM_OVERLAY_HANDLE_SIZE_PX / zoom;
    const rotOffset = 20 / zoom;

    const overlayGroup = new paper.Group({name: 'transformOverlay'});
    overlayGroup.guide = true;


    paper.project.activeLayer.addChild(overlayGroup);
    overlayGroup.matrix = new paper.Matrix();
    overlayGroup.pivot = new paper.Point(0, 0);


    const rect = new paper.Path.Rectangle(bounds);
    rect.strokeColor = new paper.Color(primaryColor);
    rect.strokeWidth = 1 / zoom;
    rect.fillColor = null;
    rect.guide = true;
    overlayGroup.addChild(rect);

    const handles = [
      {name: 'scale-tl', point: bounds.topLeft, cursor: 'nwse-resize'},
      {name: 'scale-tr', point: bounds.topRight, cursor: 'nesw-resize'},
      {name: 'scale-bl', point: bounds.bottomLeft, cursor: 'nesw-resize'},
      {name: 'scale-br', point: bounds.bottomRight, cursor: 'nwse-resize'},
      {name: 'scale-tc', point: bounds.topCenter, cursor: 'ns-resize'},
      {name: 'scale-bc', point: bounds.bottomCenter, cursor: 'ns-resize'},
      {name: 'scale-ml', point: bounds.leftCenter, cursor: 'ew-resize'},
      {name: 'scale-mr', point: bounds.rightCenter, cursor: 'ew-resize'},
    ];

    handles.forEach((h) => {
      const handle = new paper.Path.Rectangle({
        center: h.point,
        size: [handleSize, handleSize],
        fillColor: '#ffffff',
        strokeColor: primaryColor,
        strokeWidth: 1.5 / zoom,
      });
      handle.data = {isTransformHandle: true, action: h.name, cursor: h.cursor};
      handle.guide = true;

      overlayGroup.addChild(handle);
    });

    const rotPos = bounds.topCenter.add(new paper.Point(0, -rotOffset));
    const rotLine = new paper.Path.Line({
      from: bounds.topCenter,
      to: rotPos,
      strokeColor: primaryColor,
      strokeWidth: 1 / zoom,
    });
    rotLine.guide = true;
    overlayGroup.addChild(rotLine);

    const rotHandle = new paper.Path.Circle({
      center: rotPos,
      radius: handleSize / 1.5,
      fillColor: primaryColor,
      strokeColor: '#ffffff',
      strokeWidth: 1 / zoom,
    });
    rotHandle.data = {isTransformHandle: true, action: 'rotate', cursor: 'grab'};
    rotHandle.guide = true;

    overlayGroup.addChild(rotHandle);


    const mmBounds = calculateMMBounds({
      bounds: bounds,
      geometricBounds: bounds
    });

    if (mmBounds) {
      const fontSize = 16 / zoom;
      const labelOffset = 10 / zoom;
      const extraRight = 20 / zoom;

      const widthText = new paper.PointText({
        point: bounds.topCenter.add(new paper.Point(labelOffset + extraRight, -labelOffset)),
        content: `${mmBounds.widthMm.toFixed(2)} mm`,
        fillColor: primaryColor,
        fontSize: fontSize,
        fontWeight: 'bold',
        name: 'widthLabel'
      });
      widthText.guide = true;
      overlayGroup.addChild(widthText);

      const heightText = new paper.PointText({
        point: bounds.rightCenter.add(new paper.Point(labelOffset + extraRight, fontSize / 3)),
        content: `${mmBounds.heightMm.toFixed(2)} mm`,
        fillColor: primaryColor,
        fontSize: fontSize,
        fontWeight: 'bold',
        name: 'heightLabel'
      });
      heightText.guide = true;
      overlayGroup.addChild(heightText);

      const maxSide = mmBounds.maxDiameterMm;
      const maxText = new paper.PointText({
        point: bounds.topRight.add(new paper.Point(labelOffset + extraRight, -labelOffset)),
        content: `${maxSide.toFixed(2)} mm`,
        fillColor: isDark ? '#ef4444' : '#dc2626',
        fontSize: fontSize,
        fontWeight: 'bold',
        name: 'maxLabel'
      });
      maxText.guide = true;
      overlayGroup.addChild(maxText);
    }

    paper.view.update();
  }, [theme]);

  useEffect(() => {
    const handleTransform = () => updateTransformUI();

    window.addEventListener('paper-item-transform', handleTransform);
    window.addEventListener('paper-item-deleted', handleTransform);
    window.addEventListener('paper-item-visibility-changed', handleTransform);
    
    return () => {
      window.removeEventListener('paper-item-transform', handleTransform);
      window.removeEventListener('paper-item-deleted', handleTransform);
      window.removeEventListener('paper-item-visibility-changed', handleTransform);
    };
  }, [updateTransformUI]);

  return updateTransformUI;
}